import type { GameStat, GameSummary } from "../api/games";
import { GAME_TINTS, GAME_TITLES } from "../ui/gameTitles";
import { formatCount, formatLevel, formatPercent } from "../lib/format";
import { LEVEL_COUNT } from "../curriculum/progress";
import type { GameId } from "../curriculum";
import type { Lang } from "../i18n";
import {
  Panel,
  SectionHead,
  StatCard,
  StatGrid,
  TableCard,
  Td,
  Thead,
  Tr,
} from "./DashUI";
import { StatusPill } from "./DashStatus";
import { CheckIcon, InfoIcon } from "../ui/icons";

/**
 * *"What are they actually playing?"* — one panel, four tiers.
 *
 * 🔴 IT LIVES IN `shared/components` FOR THE REASON THE DASHBOARD KIT DOES: *"a
 * kit that lives in one feature folder is a kit the next tier copies badly."*
 * A parent, a teacher, a school admin and an operator all read this answer, the
 * server computes it once (`GameStatsService`), and four hand-built tables would
 * be four chances to render the same five games under four different names — and
 * one chance to render a percentage where a level is the honest answer.
 *
 * 🔴 THE DATA-DISPLAY RULES ARE ENFORCED HERE RATHER THAN REMEMBERED PER SCREEN
 * (`design-system.md` §8):
 *
 *   · **A level reads as a level, never as a percentage.** `topLevel` is drawn
 *     with `formatLevel` against `LEVEL_COUNT`. The one percentage on the panel
 *     is answer accuracy, which is not a skill reading — it is *"how often are
 *     they getting it right"*, and it is what a teacher checks when she suspects
 *     a level is too hard.
 *   · **Integers, one precision, tabular figures.** Every number goes through
 *     `formatCount` or `formatPercent`, both of which round.
 *   · **No emoji inside a number**, and none anywhere on this panel: emoji are
 *     game identity on a child's tile, not furniture on an adult's table.
 *   · **The empty state is designed.** A game nobody has opened is a row that
 *     says so, because the server returns every game rather than only the played
 *     ones — an absent row would read as *"nothing to see"* when the fact is
 *     *"nobody has started this"*.
 *
 * ⚠️ EVERY STRING IS THE CALLER'S. `formatDelta`'s lesson, applied to a whole
 * panel: a component that emits its own copy is a component whose wording no
 * tier can correct, and one that would have to be edited rather than passed to
 * if a second language ever arrives. The numbers are this file's; the words are
 * not.
 */

/** The words this panel needs, supplied by whichever tier is drawing it. */
export interface GamesPanelCopy {
  title: string;
  hint?: string;
  /** Column headers. */
  game: string;
  rounds: string;
  children: string;
  accuracy: string;
  level: string;
  lastPlayed: string;
  /** Tiles. */
  gamesPlayed: string;
  totalRounds: string;
  overallAccuracy: string;
  mostPlayed: string;
  /** `Level {level} of {of}` in the reader's language. */
  levelTemplate: string;
  /** A game nobody has opened. */
  notStarted: string;
  /** No child in the set has played anything at all. */
  nothingYet: string;
  /** `12 of 24` — the count with its denominator. */
  ofTotal: (n: number, total: number) => string;
  /** A date nobody has. */
  never: string;
}

export interface GamesPanelProps {
  games: readonly GameStat[];
  summary: GameSummary;
  /**
   * How many children the figures are over.
   *
   * 🔴 WITHOUT IT, `children` IS AN UNREADABLE NUMBER. OD-39/OD-72: no count
   * without its denominator. `undefined` is the honest answer for a single-child
   * panel, where "1 of 1" would be noise — that panel hides the column instead.
   */
  childCount?: number;
  lang: Lang;
  copy: GamesPanelCopy;
  /**
   * Whether a game nobody has opened gets a row of zeroes.
   *
   * 🔴 IT DEFAULTS TO `true`, WHICH IS THE BEHAVIOUR THIS PANEL WAS BUILT WITH
   * AND THE RIGHT ONE FOR A CLASS. The header states the rule: *"a game nobody
   * has opened is a row that says so, because an absent row would read as
   * 'nothing to see' when the fact is 'nobody has started this'."* For a teacher
   * with twenty-four children that is exactly right — five rows, one of them at
   * zero, and the zero is the finding.
   *
   * 🔴 IT IS `false` ON THE PLATFORM TIER, AND THE REASON IS THAT THE SAME ROW
   * MEANS SOMETHING ELSE THERE. Across every school over thirty days, a row of
   * zeroes beside four rows of real figures does not read as a finding — it
   * reads as sample data somebody left in, which is precisely the accusation
   * `GameStatsService` and this file spend their headers defending against.
   *
   * ⚠️ NOTHING IS HIDDEN EITHER WAY. The untouched games are NAMED under the
   * table in both modes, by the line that already existed for it. What changes
   * is whether they also occupy a row of the table.
   */
  unplayedRows?: boolean;
  /**
   * How loud the four summary tiles above the table are.
   *
   * 🔴 IT EXISTS BECAUSE THIS PANEL IS THE POINT OF ONE SCREEN AND A FOOTNOTE ON
   * ANOTHER. On Reports the games table IS the page, and `md` tiles are its
   * headline. On the class overview it sits third, under a six-metric strip the
   * teacher reads first — and `md` there made *"5 of 5"* the largest figure on a
   * screen whose actual measures are the row above it. Size is the only thing
   * that says which of two summaries is the summary.
   *
   * ⚠️ IT DEFAULTS TO `md`, SO EVERY EXISTING CALLER IS UNCHANGED — parent,
   * school, platform and Reports all still draw the tile they drew.
   */
  tileSize?: "md" | "sm";
}

export function GamesPanel({
  games,
  summary,
  childCount,
  lang,
  copy,
  unplayedRows = true,
  tileSize = "md",
}: GamesPanelProps) {
  /*
   * ⚠️ THE TILES COME FROM `summary`, WHICH THE SERVER COMPUTED FROM THESE SAME
   * ROWS. Recomputing them here would be the second implementation that
   * eventually disagrees with the table below it — `design-system.md` §8:
   * sibling panels cannot contradict.
   */
  const played = summary.gamesPlayed > 0;

  /*
   * ⚠️ "UNTOUCHED" IS `rounds === 0 && children === 0`, AND IT IS THE SAME TEST
   * THE SENTENCE UNDER THE TABLE ALREADY USED. Written once here so a row can
   * never be dropped from the table while its name is missing from the line
   * beneath it — which would be the one shape that really does hide a game.
   */
  const untouched = games.filter((g) => g.rounds === 0 && g.children === 0);
  const rows = unplayedRows
    ? games
    : games.filter((g) => !(g.rounds === 0 && g.children === 0));

  return (
    <section className="font-dash text-dash-sm text-ink">
      {copy.title ? (
        <SectionHead hint={copy.hint}>{copy.title}</SectionHead>
      ) : null}

      <StatGrid>
        <StatCard
          label={copy.gamesPlayed}
          value={copy.ofTotal(summary.gamesPlayed, summary.gamesAvailable)}
          icon={<CheckIcon size={18} />}
          tone="primary"
          size={tileSize}
          source={{
            what: "Games in which the children shown here have anything saved — an answer, a movement or a finished round — out of every game Kalqy has. All time, unless this screen names a period.",
            from: "Answers, movements and rounds the games save",
          }}
        />
        <StatCard
          label={copy.totalRounds}
          value={formatCount(summary.rounds)}
          icon={<InfoIcon size={18} />}
          tone="indigo"
          size={tileSize}
          source={{
            what: "Times the children shown here finished a level of a game, across every game — finishing the same level again counts again. All time, unless this screen names a period.",
            from: "Rounds the games save as finished",
          }}
        />
        {/* ⚠️ `null` ACCURACY IS AN EM DASH, NOT 0%. Nobody has answered a
            question yet, which is not the same as getting them all wrong. */}
        <StatCard
          label={copy.overallAccuracy}
          value={
            summary.accuracyPct === null
              ? "—"
              : formatPercent(summary.accuracyPct)
          }
          icon={<InfoIcon size={18} />}
          tone="teal"
          size={tileSize}
          source={{
            what: "Right answers as a share of every answer the children shown here gave, all games together. All time, unless this screen names a period.",
            from: "Answers children gave in games",
          }}
        />
        <StatCard
          label={copy.mostPlayed}
          value={
            summary.mostPlayed === null
              ? "—"
              : titleOf(summary.mostPlayed, lang)
          }
          icon={<CheckIcon size={18} />}
          tone="leaf"
          size={tileSize}
          source={{
            what: "The game in which the children shown here finished the most rounds; a tie is settled by a fixed order of the games. Time spent and rounds left unfinished do not count.",
            from: "Rounds the games save as finished",
          }}
        />
      </StatGrid>

      {/*
        🔴 THE GAP THE STAT ROW AND THE TABLE HAD BETWEEN THEM WAS ZERO, AND THE
        CARDS READ AS SITTING ON TOP OF THE TABLE.

        `StatGrid` carries no outer margin on purpose — every other caller spaces
        it themselves (`OurPlanScreen` wraps what follows in `mt-4`,
        `SchoolScreens` in its own block) — and this one did not, so
        `</StatGrid>` was immediately followed by a bordered card. Touching
        borders alone would look tight; the stat tiles also cast `shadow-card`
        DOWNWARD, so the shadow fell across the table's top edge and the two
        containers genuinely overlapped rather than merely abutting.

        ⚠️ THE VALUE IS `StatGrid`'S OWN GUTTER, NOT A NUMBER THAT LOOKED RIGHT.
        The grid separates its tiles by `gap-3 sm:gap-4`; the table is the next
        thing in that same stack, so one gutter below the row is what makes the
        whole block one rhythm instead of a row of cards and a separate table
        that happen to be near each other.

        ⚠️ IT WRAPS THE TERNARY RATHER THAN EITHER BRANCH. The empty state is a
        `Panel` — also bordered, also flush — so a margin on the table alone
        would fix the case a teacher with data sees and leave the collision in
        place for the class that has never played.
      */}
      <div className="mt-3 sm:mt-4">
        {/*
          🔴 THE EMPTY STATE IS A SENTENCE, NOT AN EMPTY TABLE. `design-system.md`
          §12: design the empty state. A grid of five zero rows for a class that has
          never played tells a teacher nothing she does not already know, and buries
          the one thing she does need — that nothing has started.
        */}
        {!played ? (
          <Panel>
            <p className="text-muted">{copy.nothingYet}</p>
          </Panel>
        ) : (
          <TableCard>
            <table className="w-full border-collapse">
              <Thead
                headers={[
                  copy.game,
                  copy.rounds,
                  ...(childCount === undefined ? [] : [copy.children]),
                  copy.accuracy,
                  copy.level,
                  copy.lastPlayed,
                ]}
              />
              <tbody>
                {rows.map((g) => (
                  <Tr key={g.game}>
                    <Td bold>
                      <span className="flex items-center gap-2">
                        {/*
                          🔴 A COLOUR SWATCH RATHER THAN AN EMOJI. Emoji are game
                          identity on a CHILD's tile; on an adult's dense table they
                          are the "emoji as a UI icon" §12 forbids. The tint is the
                          game's own skill colour from `design-tokens.css`, so a game
                          is the same colour here as on the skill bar below it.

                          ⚠️ AND IT IS NEVER THE ONLY SIGNAL — the name is beside it.
                          Colour carries no meaning alone (§2).
                        */}
                        <span
                          aria-hidden
                          className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ background: tintOf(g.game) }}
                        />
                        {titleOf(g.game, lang)}
                      </span>
                    </Td>
                    <Td num>{formatCount(g.rounds)}</Td>
                    {childCount === undefined ? null : (
                      <Td num>{copy.ofTotal(g.children, childCount)}</Td>
                    )}
                    <Td num>
                      {g.accuracyPct === null
                        ? "—"
                        : formatPercent(g.accuracyPct)}
                    </Td>
                    {/* 🔴 A LEVEL READS AS A LEVEL. "Level 2 of 3", never "67%" —
                        *"a percentage where a level is the honest answer is a
                        bug"* (§8). */}
                    <Td soft>
                      {g.topLevel === null
                        ? copy.notStarted
                        : formatLevel(
                            g.topLevel,
                            LEVEL_COUNT,
                            copy.levelTemplate,
                          )}
                    </Td>
                    <Td soft>
                      {g.lastPlayedAt === null
                        ? copy.never
                        : new Date(g.lastPlayedAt).toLocaleDateString()}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </table>
          </TableCard>
        )}
      </div>

      {/*
        ⚠️ THE UNTOUCHED GAMES, NAMED, UNDER THE TABLE. They are already rows of
        zeroes above; this is the sentence that turns "0" into an action. It is
        drawn only when there is something to say, because a permanent "0 games
        not started" line is furniture a reader learns to skip.
      */}
      {played && untouched.length > 0 && (
        <p className="mt-3 max-w-prose text-dash-xs text-muted">
          <StatusPill tone="neutral" icon={<InfoIcon size={14} />}>
            {copy.notStarted}
          </StatusPill>{" "}
          {untouched.map((g) => titleOf(g.game, lang)).join(" · ")}
        </p>
      )}
    </section>
  );
}

/**
 * A game id → its name.
 *
 * ⚠️ IT FALLS BACK TO THE ID RATHER THAN TO A BLANK. `Event.game` is a free
 * string so the content library (F4) can add a game without a migration, and the
 * server deliberately reports one it has never heard of rather than dropping it.
 * A row labelled with a slug is ugly and searchable; a row labelled with nothing
 * is a game that silently disappeared from a report.
 */
function titleOf(game: string, lang: Lang): string {
  return GAME_TITLES[game as GameId]?.[lang] ?? game;
}

/** ⚠️ A neutral swatch for a game with no assigned tint, for `titleOf`'s reason. */
function tintOf(game: string): string {
  return GAME_TINTS[game as GameId] ?? "var(--color-line-strong)";
}
